import { Navbar, Nav, Container, Dropdown } from "react-bootstrap";
import { Bell, ChevronDown, User } from "lucide-react";
import "../pages/dashboard.css";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect, useRef } from "react"; import Login from "../pages/Login";
const Header = ({ openAuthModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, role, logout, user } = useAuth();


  const [showTender, setShowTender] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [inAppAlertsEnabled, setInAppAlertsEnabled] = useState(true);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);

  const notificationDropdownRef = useRef(null);
  const bellButtonRef = useRef(null);

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    if (!isAuthenticated || !user?.id) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/notification-settings/${user.id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success && data.settings) {
        setInAppAlertsEnabled(data.settings.in_app_alerts !== undefined ? data.settings.in_app_alerts : true);
        setEmailNotificationsEnabled(data.settings.email_notifications !== undefined ? data.settings.email_notifications : true);
      }
    } catch (e) {
      console.error("Failed to fetch notification settings:", e);
    }
  };

  // Fetch unread count - only if in-app alerts are enabled
  const fetchUnreadCount = async () => {
    if (!isAuthenticated) return;

    // If in-app alerts are disabled, don't fetch count
    if (!inAppAlertsEnabled) {
      setUnreadCount(0);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/notifications/count`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success) {
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (e) {
      console.error("Failed to fetch unread count:", e);
    }
  };

  // Fetch unread notifications - only if in-app alerts are enabled
  const fetchUnreadNotifications = async () => {
    if (!isAuthenticated || !inAppAlertsEnabled) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/notifications/unread`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.notifications?.length || 0);
      }
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    if (!inAppAlertsEnabled) return;

    try {
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` }
      });

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));

      return true;
    } catch (e) {
      console.error("Failed to mark as read:", e);
      return false;
    }
  };

  const markAllAsRead = async () => {
    if (!inAppAlertsEnabled) return;

    try {
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/notifications/read-all`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` }
      });

      setNotifications([]);
      setUnreadCount(0);

      return true;
    } catch (e) {
      console.error("Failed to mark all as read:", e);
      return false;
    }
  };

  const toggleNotifications = async () => {
    setShowNotifications(!showNotifications);

    // If opening and in-app alerts are enabled, fetch notifications
    if (!showNotifications && inAppAlertsEnabled) {
      await fetchUnreadNotifications();
    }

    // If opening and in-app alerts are disabled, just show the disabled message
  };

  const handleNotificationClick = async (notification) => {
    if (!inAppAlertsEnabled) return;

    await markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
    setShowNotifications(false);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnBell = bellButtonRef.current && bellButtonRef.current.contains(event.target);
      const isClickInsideDropdown = notificationDropdownRef.current && notificationDropdownRef.current.contains(event.target);

      if (showNotifications && !isClickInsideDropdown && !isClickOnBell) {
        if (inAppAlertsEnabled) {
          markAllAsRead();
        }
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications, inAppAlertsEnabled]);

  // Fetch settings and count on mount and when user changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotificationSettings();
    }
  }, [isAuthenticated, user?.id]);

  // Fetch unread count when settings change
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();

      const intervalId = setInterval(() => {
        if (!showNotifications && inAppAlertsEnabled) {
          fetchUnreadCount();
        }
      }, 30000);

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, [isAuthenticated, inAppAlertsEnabled, showNotifications]);

  // Shared state so only one header dropdown is open at a time
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTimeoutRef = useRef(null);

  const HoverDropdown = ({ title, items, pathPrefix = "" }) => {
    const isActive = items.some(
      (item) => location.pathname.startsWith(`${pathPrefix}${item.path}`)
    );

    const isOpen = activeDropdown === title;

    const handleMouseEnter = () => {
      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
      if (window.innerWidth > 992) setActiveDropdown(title);
    };

    const handleMouseLeave = () => {
      dropdownTimeoutRef.current = setTimeout(() => {
        if (window.innerWidth > 992) setActiveDropdown(prev => prev === title ? null : prev);
      }, 50);
    };

    return (
      <Dropdown
        className="me-1"
        show={isOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Dropdown.Toggle
          as="span"
          className={`nav-link ${isActive ? "active" : ""}`}
        >
          {title}
        </Dropdown.Toggle>

        <Dropdown.Menu className="mega-menu">
          {items.map((item, index) => {
            const fullPath = `${pathPrefix}${item.path}`;
            const isItemActive = location.pathname.startsWith(fullPath);
            return (
              <Dropdown.Item
                key={index}
                onClick={() => {
                  setActiveDropdown(null);
                  navigate(fullPath);
                }}
                className={isItemActive ? "active" : ""}
              >
                {item.label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const isTenderActive =
    location.pathname === "/ActiveTenders" ||
    location.pathname === "/archivedTenders" ||
    location.pathname === "/reverse-auction-tenders";

  return (
    <Navbar expand="xl" className="smt-header">
      <Container fluid className="px-4 ">
        <Navbar.Brand
          className="fw-bold text-teal"
          as={NavLink}
          to={isAuthenticated ? "/dashboard" : "/"}
        >
          <img
            src="/images/logo.svg"
            width="196"
            height="42"
            className="d-inline-block align-middle me-2 mt-2"
            alt="Logo"
          />

        </Navbar.Brand>

        <Navbar.Toggle />

        <Navbar.Collapse className="justify-content-between">
          <Nav className="mx-auto smt-nav flex-nowrap align-items-center">

            {!isAuthenticated && (
              <>
                <Nav.Link as={NavLink} to="/" className="me-1">
                  Home
                </Nav.Link>

                <Dropdown
                  className="me-1"
                  show={showTender}
                  onMouseEnter={() => window.innerWidth > 992 && setShowTender(true)}
                  onMouseLeave={() => window.innerWidth > 992 && setShowTender(false)}
                  onToggle={(isOpen) => {
                    if (window.innerWidth <= 992) setShowTender(isOpen);
                  }}
                >
                  <Dropdown.Toggle
                    as="span"
                    className={`nav-link ${isTenderActive ? "active" : ""}`}
                    onClick={() => {
                      if (!isAuthenticated) {
                        openAuthModal();
                      } else {
                        navigate("/ActiveTenders");
                      }
                    }}
                  >
                    Explore Tenders
                  </Dropdown.Toggle>

                  <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                    <Dropdown.Item
                      onClick={() => {
                        if (!isAuthenticated) {
                          openAuthModal();
                        } else {
                          navigate("/ActiveTenders");
                        }
                      }}
                    >
                      Active Tenders
                    </Dropdown.Item>

                    <Dropdown.Item
                      onClick={() => {
                        if (!isAuthenticated) {
                          openAuthModal();
                        } else {
                          navigate("/archivedTenders");
                        }
                      }}
                    >
                      Archived Tenders
                    </Dropdown.Item>

                    <Dropdown.Item
                      onClick={() => {
                        if (!isAuthenticated) {
                          openAuthModal();
                        } else {
                          navigate("/reverse-auction-tenders");
                        }
                      }}
                    >
                      Reverse Auction
                    </Dropdown.Item>

                    {/* ✅ NEW - Awarded Tenders */}
                    <Dropdown.Item
                      onClick={() => {
                        if (!isAuthenticated) {
                          openAuthModal();
                        } else {
                          navigate("/awarded-tenders");
                        }
                      }}
                    >
                       Awarded Tenders
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Nav.Link as={NavLink} to="/services" className="me-1">
                  Services
                </Nav.Link>
              </>
            )}

            {isAuthenticated && role === "finance" ? (
              <>
                <Nav.Link as={NavLink} to="/dashboard" className="me-1">
                  Home
                </Nav.Link>

                <Nav.Link as={NavLink} to="/billing-table" className="me-1">
                  Billing Table
                </Nav.Link>

                <Nav.Link as={NavLink} to="/FinanceReport" className="me-1">
                  Report
                </Nav.Link>
                <Nav.Link as={NavLink} to="/finance-analytics" className="me-1">
                  Finance Analytics
                </Nav.Link>
              </>
            ) : isAuthenticated && (
              <>
                <Nav.Link as={NavLink} to="/dashboard" className="me-1">
                  Home
                </Nav.Link>

                <Dropdown
                  className="me-1"
                  show={showTender}
                  onMouseEnter={() => window.innerWidth > 992 && setShowTender(true)}
                  onMouseLeave={() => window.innerWidth > 992 && setShowTender(false)}
                  onToggle={(isOpen) => {
                    if (window.innerWidth <= 992) setShowTender(isOpen);
                  }}
                >
                  <Dropdown.Toggle
                    as={NavLink}
                    to="/ActiveTenders"
                    className={`nav-link ${isTenderActive ? "active" : ""}`}
                  >
                    Explore Tenders
                  </Dropdown.Toggle>
                  <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                    <Dropdown.Item as={NavLink} to="/ActiveTenders">
                      Active Tenders
                    </Dropdown.Item>

                    <Dropdown.Item as={NavLink} to="/archivedTenders">
                      Archived Tenders
                    </Dropdown.Item>

                    <Dropdown.Item as={NavLink} to="/reverse-auction-tenders">
                      Reverse Auction
                    </Dropdown.Item>
 {(role  === "admin") && (
                  
                    <Dropdown.Item as={NavLink} to="/awarded-tenders">
                      Awarded Tenders
                    </Dropdown.Item>)}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Corrected condition - checks for operations OR admin role */}
                {(role === "operations") && (
                  <Nav.Link as={NavLink} to="/upload-csv" className="me-1">
                    <i className="bi bi-file-earmark-upload me-1"></i>
                    Upload Watchlist
                  </Nav.Link>
                )}

                {role === "customer" && (
                  <Nav.Link
                    as={NavLink}
                    to="/customer-analytics"
                    className="me-1 d-flex align-items-center text-nowrap"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Customer Analytics
                  </Nav.Link>
                )}

                {role === "admin" && (
                  <>
                    <Nav.Link
                      as={NavLink}
                      to="/admin-blogs"
                      className="me-1 d-flex align-items-center text-nowrap"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Manage Blogs
                    </Nav.Link>

                    <Nav.Link
                      as={NavLink}
                      to="/admin-analytics"
                      className="me-1 d-flex align-items-center text-nowrap"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Admin Analytics
                    </Nav.Link>

                    <HoverDropdown
                      title="Administration"
                      items={[
                        { label: "Mailer List", path: "/mailer-list" },
                        { label: "Employee-Customer Assignment", path: "/emp-customer-assignment" },
                      ]}
                    />

                    <HoverDropdown
                      title="Sales"
                      items={[
                        { label: "Leads", path: "/sales-dashboard" },
                        { label: "Feedback", path: "/FeedBackSales" },
                        { label: "Services", path: "/services" },
                      ]}
                    />

                    <HoverDropdown
                      title="Operations"
                      items={[
                        { label: "Task List", path: "/TaskList" },
                      ]}
                    />

                    <HoverDropdown
                      title="Finance"
                      items={[
                        { label: "Billing Table", path: "/billing-table" },
                      ]}
                    />

                    <HoverDropdown
                      title="Reports"
                      items={[
                        { label: "Sales Reports", path: "/Report" },
                        { label: "Operations Reports", path: "/OperationsReport" },
                        { label: "Finance Reports", path: "/FinanceReport" },
                      ]}
                    />
                  </>
                )}

                {role === "sales" && (
                  <>
                    <Nav.Link as={NavLink} to="/sales-dashboard" className="me-1">
                      Leads
                    </Nav.Link>

                    <Nav.Link as={NavLink} to="/mailer-list" className="me-1">
                      Mailer List
                    </Nav.Link>

                    <Nav.Link
                      as={NavLink}
                      to="/sales-analytics"
                      className="me-1 d-flex align-items-center text-nowrap"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Sales Analytics
                    </Nav.Link>
                  </>
                )}
                {role !== "admin" && role !== "finance" && role !== "sales" && role !== "operations" && (
                  <Nav.Link as={NavLink} to="/TenderList" className="me-1">
                    Tender List
                  </Nav.Link>
                )}
                {(role?.toLowerCase() === "operations" ||

                  role?.toLowerCase() === "sales") && (
                    <>
                      <Nav.Link as={NavLink} to="/TaskList" className="me-1">
                        Task List
                      </Nav.Link>

                      <Nav.Link
                        as={NavLink}
                        to="/operations-analytics"
                        className="me-1 d-flex align-items-center text-nowrap"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Operations Analytics
                      </Nav.Link>
                    </>
                  )}

                {(role === "sales" || role === "operations") && (
                  <Nav.Link
                    as={NavLink}
                    to={role === "operations" ? "/OperationsReport" : "/Report"}
                    className="me-1"
                  >
                    Report
                  </Nav.Link>
                )}

                {role === "owner" && (
                  <HoverDropdown
                    title="Analytics"
                    items={[
                      { label: "Competitor List", path: "/analytics/competitors" },
                      { label: "Bid Chart", path: "/analytics/bid-chart" },
                      { label: "Industry Trends", path: "/analytics/trends" },
                    ]}
                  />
                )}

                {role !== "admin" && role !== "operations" && (
                  <Nav.Link as={NavLink} to="/services" className="me-1">
                    Services
                  </Nav.Link>
                )}
              </>
            )}
          </Nav>

          <div className="d-flex align-items-center gap-4">

            {!isAuthenticated && (
              <>
                <Nav.Link as={NavLink} to="/contactUs" style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  Contact Us
                </Nav.Link>

                <Nav.Link
                  as={NavLink}
                  to="/login"
                  className="btn btn-sm"
                  style={{
                    background: "#2F7F8F",
                    color: "white",
                    borderRadius: "20px",
                    padding: "5px 15px",
                    textDecoration: "none"
                  }}
                >
                  Register / Login
                </Nav.Link>
              </>
            )}

            {isAuthenticated && (
              <>
                {role !== "sales" && (
                  <Nav.Link as={NavLink} to="/contactUs" style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    Contact Us
                  </Nav.Link>
                )}

                {/* ✅ Bell Icon - Always visible, but behavior changes based on inAppAlertsEnabled */}
                <div className="position-relative" ref={notificationDropdownRef}>
                  <div
                    ref={bellButtonRef}
                    onClick={toggleNotifications}
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      opacity: inAppAlertsEnabled ? 1 : 0.6
                    }}
                    className="hover-bg-light"
                    title={!inAppAlertsEnabled ? "In-app alerts are disabled" : "Notifications"}
                  >
                    <Bell size={20} />
                    {/* Show badge only if in-app alerts are enabled and there are unread notifications */}
                    {inAppAlertsEnabled && unreadCount > 0 && !showNotifications && (
                      <span className="badge rounded-circle position-absolute" style={{
                        backgroundColor: "#2F7F8F",
                        top: "-8px",
                        right: "-10px",
                        fontSize: "0.65rem",
                        padding: "3px 5px",
                        minWidth: "20px",
                        textAlign: "center"
                      }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    {/* Small indicator when disabled */}
                    {!inAppAlertsEnabled && (
                      <span style={{
                        position: "absolute",
                        bottom: "-2px",
                        right: "-2px",
                        fontSize: "0.5rem",
                        color: "#6c757d"
                      }}>
                        ⚠
                      </span>
                    )}
                  </div>

                  {showNotifications && (
                    <div
                      className="dropdown-menu show"
                      style={{
                        width: "380px",
                        maxHeight: "450px",
                        overflowY: "auto",
                        position: "absolute",
                        right: 0,
                        top: "100%",
                        marginTop: "8px",
                        padding: 0,
                        borderRadius: "8px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                        zIndex: 1050
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom" style={{ background: "#f8f9fa", borderRadius: "8px 8px 0 0" }}>
                        <span className="fw-bold text-dark">
                          Notifications
                          {inAppAlertsEnabled && unreadCount > 0 && (
                            <span className="ms-2 badge" style={{ fontSize: "0.7rem", backgroundColor: "#2F7F8F" }}>
                              {unreadCount} new
                            </span>
                          )}
                          {!inAppAlertsEnabled && (
                            <span className="ms-2 badge bg-secondary" style={{ fontSize: "0.7rem" }}>
                              Disabled
                            </span>
                          )}
                        </span>
                        <div className="d-flex gap-2">
                          {inAppAlertsEnabled && notifications.length > 0 && (
                            <span
                              style={{
                                fontSize: "0.7rem",
                                cursor: "pointer",
                                color: "#2F7F8F",
                                background: "#e8f4f3",
                                padding: "2px 12px",
                                borderRadius: "12px"
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await markAllAsRead();
                              }}
                            >
                              Mark all read
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: "1.2rem",
                              cursor: "pointer",
                              color: "#6c757d",
                              padding: "2px 8px"
                            }}
                            onClick={() => setShowNotifications(false)}
                          >
                            ✕
                          </span>
                        </div>
                      </div>

                      {!inAppAlertsEnabled ? (
                        <div className="text-center py-4 px-3">
                          <Bell size={32} className="text-muted mb-2" style={{ opacity: 0.4 }} />
                          <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>In-app alerts are disabled</p>
                          <p className="text-muted" style={{ fontSize: "0.8rem" }}>Enable them in your profile settings</p>
                        </div>
                      ) : loading ? (
                        <div className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-secondary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="text-center py-4 px-3">
                          <Bell size={32} className="text-muted mb-2" />
                          <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>No new notifications</p>
                          <p className="text-muted" style={{ fontSize: "0.8rem" }}>You're all caught up!</p>
                        </div>
                      ) : (
                        notifications.map((notif, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleNotificationClick(notif)}
                            style={{
                              cursor: "pointer",
                              background: "#f0f7ff",
                              borderBottom: "1px solid #f0f0f0",
                              padding: "12px 16px",
                              transition: "background 0.2s",
                              position: "relative",
                              borderLeft: "3px solid #2F7F8F"
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#e6f0fa"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "#f0f7ff"}
                          >
                            <div className="fw-bold text-dark" style={{ fontSize: "0.9rem", marginBottom: "4px" }}>
                              {notif.title || "New Notification"}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                              {notif.message || "You have a new notification"}
                            </div>
                            <div className="text-muted mt-1" style={{ fontSize: "0.65rem" }}>
                              {new Date(notif.created_at).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </div>
                            <div style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: "#2F7F8F",
                              position: "absolute",
                              right: "16px",
                              top: "50%",
                              transform: "translateY(-50%)"
                            }} />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <Dropdown align="end">
                  <Dropdown.Toggle className="user-dropdown-toggle d-flex align-items-center p-0">
                    <User size={20} className="me-2" />
                    <span className="me-2">
                      {user?.full_name || user?.name || "User"}
                    </span>
                    <ChevronDown size={16} />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item as={NavLink} to="/profile">
                      Profile
                    </Dropdown.Item>

                    {/* {role === "admin" && (
                      <Dropdown.Item as={NavLink} to="/user-management">
                        User Management
                      </Dropdown.Item>
                    )} */}

                    {role?.toLowerCase().includes("customer") && (
                      <>
                        <Dropdown.Item as={NavLink} to="/mytender">
                          My Tenders
                        </Dropdown.Item>

                        <Dropdown.Item as={NavLink} to="/subscription">
                          Subscription
                        </Dropdown.Item>

                        <Dropdown.Item as={NavLink} to="/Feedback">
                          Feedback
                        </Dropdown.Item>
                      </>
                    )}

                    {role === "operations" && (
                      <></>
                    )}

                    {role === "sales" && (
                      <Dropdown.Item
                        onClick={() => navigate("/FeedBackSales")}
                      >
                        Feedback
                      </Dropdown.Item>
                    )}

                    <Dropdown.Divider />

                    <Dropdown.Item
                      onClick={() => {
                        logout();
                        navigate("/dashboard");
                      }}
                    >
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            )}
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;